'use client';

import * as React from 'react';
import Link from 'next/link';
import { BadgeCheck } from 'lucide-react';

import { cdnImageUrl } from '@kit/shared/storage';

import { cn } from '../lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../shadcn/tooltip';

// Locked tokens (DESIGN.md § Card & Page Patterns). Consumers cannot override.
const SURFACE_HOVER_BEZIER = 'cubic-bezier(0.32, 0.72, 0, 1)';
const PHOTO_ZOOM_BEZIER = 'cubic-bezier(0.16, 1, 0.3, 1)';
const VERIFIED_COLOR = 'oklch(0.74 0.08 80)';

export type EntityCardAspect = '3/4' | '3/1' | '16/9' | '1/1';

const ASPECT_CLASS: Record<EntityCardAspect, string> = {
  '3/4': 'aspect-[3/4]',
  '3/1': 'aspect-[3/1]',
  '16/9': 'aspect-[16/9]',
  '1/1': 'aspect-square',
};

interface EntityCardContextValue {
  aspect: EntityCardAspect;
  hasCoinMark: boolean;
  setHasCoinMark: (v: boolean) => void;
  fallbackSeed: string;
  testId?: string;
}

const EntityCardContext = React.createContext<EntityCardContextValue | null>(
  null,
);

function useEntityCardContext(componentName: string): EntityCardContextValue {
  const ctx = React.useContext(EntityCardContext);
  if (!ctx) {
    throw new Error(`${componentName} must be used inside <EntityCard>`);
  }
  return ctx;
}

// Deterministic gradient fallback when no cover image exists.
// Same algorithm as the original DancerCard / OrganizationCard fallbacks,
// extracted so all entity cards share one fallback look.
export function hashSeededHueOffset(seed: string): number {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return (hash % 25) - 12;
}

export function hashSeededGradient(seed: string): string {
  const offset = hashSeededHueOffset(seed);
  return `linear-gradient(155deg,
    oklch(0.30 0.04 ${22 + offset}) 0%,
    oklch(0.20 0.025 ${30 + offset}) 100%)`;
}

interface EntityCardRootProps {
  href: string;
  aspect: EntityCardAspect;
  ariaLabel: string;
  // Used to seed the fallback gradient and snapshot tests. Pass the entity's
  // ID or slug so the gradient is stable across renders.
  fallbackSeed: string;
  className?: string;
  testId?: string;
  // Footer region — rendered as a SIBLING of the card Link, inside the card
  // surface. Passed as a PROP (not a discovered child) on purpose: child
  // discovery by `displayName` is unreliable across the RSC boundary — during
  // SSR a client-reference proxy exposes no `.displayName`, so the slot would
  // land inside the Link on the server and outside on the client, producing a
  // nested-<a> hydration mismatch. A prop serializes deterministically. Wrap
  // the content in <EntityCardFooter> for the standard divider + padding.
  footer?: React.ReactNode;
  children: React.ReactNode;
}

function EntityCardRoot({
  href,
  aspect,
  ariaLabel,
  fallbackSeed,
  className,
  testId,
  footer,
  children,
}: EntityCardRootProps) {
  const [hasCoinMark, setHasCoinMark] = React.useState(false);

  const ctx = React.useMemo<EntityCardContextValue>(
    () => ({ aspect, hasCoinMark, setHasCoinMark, fallbackSeed, testId }),
    [aspect, hasCoinMark, fallbackSeed, testId],
  );

  // The bookmark slot is absolutely positioned OUTSIDE the Link (it fires its
  // own action; nested-link is invalid HTML). It is discovered by displayName
  // here for source-compat; the footer takes the safer prop route (see the
  // `footer` prop docs) because it carries interactive controls whose
  // server/client placement must match exactly.
  const childArray = React.Children.toArray(children);
  const bookmarkSlot = childArray.find((c) => {
    if (!React.isValidElement(c)) return false;
    // React.isValidElement only checks $$typeof, not that .type is set or is an
    // object. Fragments (Symbol), intrinsic strings ('div'), forwardRef wrappers,
    // and React 19 RSC element shapes can all leave c.type as a non-object or
    // undefined. Guard before reading .displayName. Sentry BALLEE-D? (2026-05-23 +
    // 2026-05-24): TypeError on flow.ballee.co/marketplace when an authenticated
    // user's chrome wraps EntityCard children differently than the anon path.
    const t = c.type as unknown;
    if (!t || (typeof t !== 'function' && typeof t !== 'object')) return false;
    return (
      (t as { displayName?: string }).displayName === 'EntityCard.Bookmark'
    );
  });
  const linkChildren = childArray.filter((c) => c !== bookmarkSlot);

  return (
    <EntityCardContext.Provider value={ctx}>
      <article
        className={cn(
          'group relative isolate flex h-full flex-col overflow-hidden',
          'rounded-md',
          // Solid surface (one warm-bone step off the canvas) so the card
          // reads on BOTH light and dark. The previous translucent
          // `from-card/70 to-card/30` faded to near-transparent at the
          // bottom and vanished entirely in light mode where --card ==
          // --background (the "card has no bottom" defect). Hover brightens
          // toward --card-hover — theme-correct in both registers.
          // DESIGN.md § Card & Page Patterns.
          'bg-card hover:bg-card-hover',
          'transition-[background-color] duration-300',
          className,
        )}
        style={{ transitionTimingFunction: SURFACE_HOVER_BEZIER }}
        data-testid={testId}
        data-aspect={aspect}
      >
        {bookmarkSlot}

        <Link
          href={href}
          className="flex min-h-0 flex-1 flex-col outline-none"
          aria-label={ariaLabel}
        >
          {linkChildren}
        </Link>

        {footer}
      </article>
    </EntityCardContext.Provider>
  );
}

// Coin-mark spec — small logo straddling the cover/caption seam.
// Organizations only. Provided via the `coinMark` prop on <Cover>, NOT a
// separate slot, so the two share the same relative wrapper.
export interface EntityCardCoinMarkSpec {
  src: string | null | undefined;
  alt: string;
  fallbackInitials: string;
  // 56px default; consumers shouldn't change this unless the aspect changes.
  size?: number;
  priority?: boolean;
}

// Cover photo. Renders next/image when src is provided; falls back to a
// hash-seeded gradient with the entity's initials when not. Optionally
// renders a coin-mark logo straddling the seam (organizations only).
interface EntityCardCoverProps {
  src: string | null | undefined;
  alt: string;
  fallbackInitials: string;
  priority?: boolean;
  sizes?: string;
  // Pin slot — top-left overlay on the cover photo. Used for the mono
  // "FEATURED" kicker. Coral is BANNED here per DESIGN.md.
  pin?: React.ReactNode;
  // Overlay slot — free-form absolutely-positioned node over the cover (the
  // consumer sets its own position, e.g. an eligibility chip top-right). Kept
  // separate from `pin` (which owns the mono top-left treatment).
  overlay?: React.ReactNode;
  // Crop anchor. Defaults to 'top' on 3/4 (portrait people photos — keep
  // heads), 'center' otherwise. Override to 'center' for 3/4 POSTER art where
  // the baked-in title can sit top OR bottom and a top-anchored crop would
  // still behead it (see docs/architecture/cover-image-display.md).
  objectPosition?: 'top' | 'center';
  // Bottom scrim — a soft dark gradient over the lower ~45% of the cover so an
  // overlaid pin (or any future date-pill) stays legible on light poster art.
  scrim?: boolean;
  // Coin-mark spec — when present, switches Caption to centered alignment
  // with extra top-padding to clear the half-outset logo.
  coinMark?: EntityCardCoinMarkSpec;
  className?: string;
}

function EntityCardCover({
  src,
  alt,
  fallbackInitials,
  priority = false,
  // `sizes` stays in the public prop type for source-compat but is no longer
  // consumed: cover images render via a plain <img> + cdnImageUrl (rotating
  // signed/R2 URLs can't go through next/image — see image-rendering.md), and
  // <img> has no responsive `sizes` hint to forward here.
  pin,
  overlay,
  objectPosition,
  scrim = false,
  coinMark,
  className,
}: EntityCardCoverProps) {
  const { aspect, fallbackSeed, setHasCoinMark } =
    useEntityCardContext('EntityCard.Cover');

  // Inform Caption to switch alignment when a coin-mark is present.
  const hasCoinMark = !!coinMark;
  React.useEffect(() => {
    setHasCoinMark(hasCoinMark);
    return () => setHasCoinMark(false);
  }, [hasCoinMark, setHasCoinMark]);

  const coinSize = coinMark?.size ?? 56;
  const coinOffset = Math.round(coinSize / 4); // -14px for default 56

  // Fade the cover in once it decodes, over the `bg-muted` panel, instead
  // of letting the browser paint it top-down (the raw-<img> streaming look).
  // Transform (hover zoom) and opacity (load fade) share one transition so
  // they don't fight over `transition-property`; the zoom keeps its bezier.
  const [loaded, setLoaded] = React.useState(false);
  // No-void rule (image-rendering.md §5): a cover that fails to load must
  // degrade to the neutral gradient panel, never the browser broken-image
  // glyph + alt text. Cold/slow Supabase originals and transient origin
  // errors DO fail in the field under a burst of cover loads.
  const [errored, setErrored] = React.useState(false);
  const imgRef = React.useRef<HTMLImageElement>(null);
  // A cached image can finish loading before React attaches `onLoad`/`onError`,
  // which would strand it at opacity-0 (success) or leave the glyph showing
  // (failure). Reconcile from `.complete` + `naturalWidth` on mount:
  // naturalWidth === 0 on a completed image means it failed to decode.
  React.useEffect(() => {
    const img = imgRef.current;
    if (!img?.complete) return;
    if (img.naturalWidth > 0) setLoaded(true);
    else setErrored(true);
  }, []);

  const showFallback = !src || errored;

  return (
    <div className={cn('relative', className)}>
      <div
        className={cn(
          'bg-muted relative w-full overflow-hidden',
          ASPECT_CLASS[aspect],
        )}
      >
        {!showFallback ? (
          /* eslint-disable-next-line @next/next/no-img-element */
          <img
            ref={imgRef}
            src={cdnImageUrl(src, 'GALLERY') ?? src!}
            alt={alt}
            loading={priority ? 'eager' : 'lazy'}
            fetchPriority={priority ? 'high' : undefined}
            decoding="async"
            onLoad={() => setLoaded(true)}
            onError={() => setErrored(true)}
            className={cn(
              'absolute inset-0 h-full w-full object-cover',
              // Crop anchor: explicit prop wins; else 3/4 defaults to top
              // (people photos), everything else to center.
              (objectPosition ?? (aspect === '3/4' ? 'top' : 'center')) ===
                'top' && 'object-[center_top]',
              'transition-[opacity,transform] duration-700',
              loaded ? 'opacity-100' : 'opacity-0',
              'group-hover:scale-[1.025]',
            )}
            style={{ transitionTimingFunction: PHOTO_ZOOM_BEZIER }}
          />
        ) : (
          <div
            className="relative flex size-full items-center justify-center"
            style={{ background: hashSeededGradient(fallbackSeed) }}
            aria-hidden
          >
            <span className="font-heading text-[40px] leading-none font-medium tracking-tight text-white/60 italic">
              {fallbackInitials}
            </span>
          </div>
        )}

        {scrim && !showFallback && (
          <div
            className="absolute inset-x-0 bottom-0 z-10 h-[45%] bg-gradient-to-t from-black/55 via-black/20 to-transparent"
            aria-hidden
          />
        )}

        {pin && (
          <div
            className={cn(
              'absolute top-2 left-2 z-20',
              'font-mono text-[10px] tracking-[0.16em] uppercase',
              'text-white/95 mix-blend-difference',
            )}
          >
            {pin}
          </div>
        )}

        {overlay}
      </div>

      {coinMark && (
        <div
          className={cn(
            'absolute left-1/2 z-20 -translate-x-1/2',
            'overflow-hidden rounded-full',
            'ring-card bg-card ring-4',
          )}
          style={{
            width: coinSize,
            height: coinSize,
            bottom: -coinOffset,
          }}
        >
          {coinMark.src ? (
            /* eslint-disable-next-line @next/next/no-img-element */
            <img
              src={cdnImageUrl(coinMark.src, 'THUMBNAIL') ?? coinMark.src}
              alt={coinMark.alt}
              width={coinSize}
              height={coinSize}
              loading={coinMark.priority ? 'eager' : 'lazy'}
              className="size-full object-cover"
            />
          ) : (
            <div className="bg-muted text-muted-foreground flex size-full items-center justify-center">
              <span className="font-heading text-[14px] font-medium tracking-[0.02em]">
                {coinMark.fallbackInitials}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
EntityCardCover.displayName = 'EntityCard.Cover';

// Bookmark slot — quiet until pointed at. Renders ABOVE the Link via the
// root component's split logic.
function EntityCardBookmark({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'absolute top-2 right-2 z-30',
        'opacity-0 transition-opacity duration-200',
        'group-focus-within:opacity-100 group-hover:opacity-100',
        className,
      )}
    >
      {children}
    </div>
  );
}
EntityCardBookmark.displayName = 'EntityCard.Bookmark';

// Caption block. Auto-switches to center alignment + top-pad when a
// coin-mark is present in the same card.
function EntityCardCaption({ children }: { children: React.ReactNode }) {
  const { hasCoinMark } = useEntityCardContext('EntityCard.Caption');

  return (
    <div
      className={cn(
        'flex flex-1 flex-col',
        hasCoinMark
          ? 'items-center gap-1.5 px-5 pt-10 pb-5 text-center'
          : 'gap-1 px-3 pt-3 pb-3',
      )}
    >
      {children}
    </div>
  );
}
EntityCardCaption.displayName = 'EntityCard.Caption';

// Sentient headline with optional verified badge. Italic when verified is
// true. Headline size is locked per DESIGN.md: 15px for portraits, 18px
// when a coin-mark is present (orgs); we use 18px when caption is centered
// (presence of coin-mark) and 15px otherwise.
interface EntityCardTitleProps {
  children: React.ReactNode;
  verified?: boolean;
  verifiedLabel?: string;
  // Display title text (for tooltip on long names). Defaults to the children
  // when children is a string.
  title?: string;
}

function EntityCardTitle({
  children,
  verified = false,
  verifiedLabel = 'Verified',
  title,
}: EntityCardTitleProps) {
  const { hasCoinMark } = useEntityCardContext('EntityCard.Title');
  const sizeClass = hasCoinMark
    ? 'text-[18px] line-clamp-2'
    : 'text-[15px] line-clamp-1';

  const titleAttr =
    title ??
    (typeof children === 'string' && children.length > 26
      ? children
      : undefined);

  return (
    <h3
      className={cn(
        'font-heading text-foreground leading-[1.2] tracking-[-0.01em]',
        sizeClass,
        verified ? 'font-medium italic' : 'font-medium',
      )}
      title={titleAttr}
    >
      {children}
      {verified && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className="ml-1 inline-flex translate-y-[2px] align-baseline"
                aria-label={verifiedLabel}
              >
                <BadgeCheck
                  className={hasCoinMark ? 'size-[14px]' : 'size-[12px]'}
                  style={{ color: VERIFIED_COLOR }}
                />
              </span>
            </TooltipTrigger>
            <TooltipContent>{verifiedLabel}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </h3>
  );
}
EntityCardTitle.displayName = 'EntityCard.Title';

// Sub-line below the headline. Muted, single line.
function EntityCardSub({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const { hasCoinMark } = useEntityCardContext('EntityCard.Sub');
  return (
    <p
      className={cn(
        'text-muted-foreground line-clamp-1',
        hasCoinMark ? 'text-[13px]' : 'text-[12px]',
        className,
      )}
    >
      {children}
    </p>
  );
}
EntityCardSub.displayName = 'EntityCard.Sub';

// Identity byline — a small rounded avatar + name, the caption's identity row
// (org / author / seller). The card-scale answer to "who owns this", replacing
// ad-hoc icon+text lines: real brand recognition without occluding the cover
// like a floating over-image chip (docs/architecture/cover-image-display.md).
// `avatarUrl` falls back to `fallbackIcon` (e.g. a Building2) when absent.
function EntityCardByline({
  name,
  avatarUrl,
  fallbackIcon,
  className,
}: {
  name: string;
  avatarUrl?: string | null;
  fallbackIcon?: React.ReactNode;
  className?: string;
}) {
  const { hasCoinMark } = useEntityCardContext('EntityCard.Byline');
  return (
    <span
      className={cn(
        'text-muted-foreground mt-1 flex min-w-0 items-center gap-1.5',
        hasCoinMark ? 'justify-center text-[13px]' : 'text-[12px]',
        className,
      )}
    >
      {avatarUrl ? (
        /* eslint-disable-next-line @next/next/no-img-element -- small avatar via cdnImageUrl (image-rendering.md forbids next/image on rotating media URLs) */
        <img
          src={cdnImageUrl(avatarUrl, 'THUMBNAIL') ?? avatarUrl}
          alt=""
          loading="lazy"
          decoding="async"
          className="size-[18px] shrink-0 rounded-full object-cover"
        />
      ) : (
        <span
          className="text-muted-foreground/70 flex size-[18px] shrink-0 items-center justify-center"
          aria-hidden
        >
          {fallbackIcon}
        </span>
      )}
      <span className="truncate">{name}</span>
    </span>
  );
}
EntityCardByline.displayName = 'EntityCard.Byline';

// Mono kicker at the bottom of the caption. Enforces uppercase tracked mono.
// Consumers cannot override the typographic treatment.
function EntityCardKicker({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const { hasCoinMark } = useEntityCardContext('EntityCard.Kicker');
  return (
    <p
      className={cn(
        'text-muted-foreground/60 line-clamp-1 font-mono tracking-[0.14em] uppercase',
        hasCoinMark ? 'mt-1.5 text-[10px]' : 'mt-0.5 text-[9.5px]',
        className,
      )}
    >
      {children}
    </p>
  );
}
EntityCardKicker.displayName = 'EntityCard.Kicker';

// Footer region — rendered by the root as a SIBLING of the Link (inside the
// card surface), separated by a hairline divider. This is the home for
// per-entity INTERACTIVE controls (a rating launcher that opens a drawer, a
// message button) which must not be descendants of the card Link: a nested
// link / button-in-link is invalid HTML and was the root cause of Sentry
// 7503288889. Keeping them here unifies them into the card surface instead of
// floating disconnected blocks below it, while staying HTML-valid.
//
// Optional and opt-in: cards that don't pass a Footer (dancer / organization /
// partner) render exactly as before. The divider tints toward the brand hue
// via `border-border` (warm in dark, per "dark is warm, never gray").
function EntityCardFooter({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'border-border/60 flex flex-col gap-2 border-t px-3 py-2.5',
        className,
      )}
    >
      {children}
    </div>
  );
}
EntityCardFooter.displayName = 'EntityCard.Footer';

// Named exports for each sub-component.
//
// Previously these were attached as runtime properties on the EntityCard
// namespace object (`EntityCard.Cover = EntityCardCover;` etc). That
// pattern crashed at runtime whenever a *server component* imported
// EntityCard: Next 16's client-reference proxy preserves the default
// exported function but NOT runtime-attached properties, so every
// `<EntityCard.Cover />` in an RSC resolved to `<undefined />` and
// React threw "Element type is invalid". Production /marketplace crashed
// for ~24h before the root cause was diagnosed (Sentry 7503288889 +
// 7501947985; hotfix PR #931 added 'use client' to ListingCard as a
// stopgap).
//
// Named exports cross the RSC client-reference boundary cleanly because
// each one is a top-level binding the bundler can statically analyse.
// Server components can now import any subset without forcing 'use client'.
//
// Consumers should migrate from `<EntityCard.X />` to `<EntityCardX />`.
// The namespace shape is preserved below as a deprecated re-export so
// existing call sites keep working during the migration; remove it once
// every consumer has been updated (grep for `EntityCard\.[A-Z]`).
export {
  EntityCardRoot as EntityCard,
  EntityCardCover,
  EntityCardBookmark,
  EntityCardCaption,
  EntityCardTitle,
  EntityCardSub,
  EntityCardByline,
  EntityCardKicker,
  EntityCardFooter,
};
